import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { client } from '../lib/sanity';
import { PortableText } from '@portabletext/react';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.fetch(
      `*[_type == "post" && slug.current == $slug][0]{
        _id,
        title,
        excerpt,
        "image": mainImage.asset->url,
        "author": author->name,
        publishedAt,
        readTime,
        "categories": categories[]->title,
        "content": body
      }`,
      { slug }
    ).then((data) => {
    console.log("Sanity single post:", data);
    setPost(data);
    setLoading(false);
  }).catch((error) => {
    console.error("Sanity single post error:", error);
    setLoading(false);
  });
}, [slug]);

  if (loading) {
    return <div style={{ padding: '80px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const hasStringContent =
  typeof post.content === 'string' && post.content.trim().length > 0;

const hasArrayContent =
  Array.isArray(post.content) && post.content.length > 0;
const portableTextComponents = {
  list: {
    bullet: ({ children }) => (
      <ul
        style={{
          listStyleType: 'disc',
          paddingLeft: '32px',
          marginBottom: '24px'
        }}
      >
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol
        style={{
          listStyleType: 'decimal',
          paddingLeft: '32px',
          marginBottom: '24px'
        }}
      >
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li style={{ marginBottom: '12px', lineHeight: '1.7' }}>{children}</li>
    ),
    number: ({ children }) => (
      <li style={{ marginBottom: '12px', lineHeight: '1.7' }}>{children}</li>
    ),
  },
};
  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
  background: 'var(--bg-secondary)',
  padding: '64px 0 48px',
  borderBottom: '1px solid var(--border-light)'
}}>
        <div className="container">
          <div style={{ marginBottom: '24px' }}>
  <Link
    to="/blog"
    className="btn-secondary"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px'
    }}
  >
    <ArrowLeft size={16} />
    Back to Blog
  </Link>

  {post.categories?.length > 0 && (
    <div
      style={{
        display: 'block',
        width: 'fit-content',
        padding: '6px 16px',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-light)',
        borderRadius: '0px'
      }}
    >
      <span className="body-small" style={{ color: 'var(--text-primary)' }}>
        {post.categories.join(', ')}
      </span>
    </div>
  )}
</div>
          
          <h1
  className="hero-large"
  style={{
    marginTop: '8px',
    marginBottom: '24px',
    maxWidth: '900px',
    lineHeight: '1.15'
  }}
>
            {post.title}
          </h1>
          
          <div style={{
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={16} style={{ color: 'var(--text-secondary)' }} />
              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {post.author || 'The Eternal Care Team'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={16} style={{ color: 'var(--text-secondary)' }} />
              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : 'Recently published'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={16} style={{ color: 'var(--text-secondary)' }} />
              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {post.readTime || '5 min read'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {(post.image || '/fallback-blog.jpg') && (
  <section style={{ padding: '24px 0 0' }}>
  <div className="container">
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}
    >
      <img
        src={post.image || '/fallback-blog.jpg'}
        alt={post.title}
        style={{
          width: '100%',
          height: '360px',
          objectFit: 'cover',
          display: 'block'
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    </div>
  </div>
</section>
)}

      {/* Content */}
      <section className="section-padding">
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div>
  

  {hasStringContent ? (
  <div
    className="blog-content"
    style={{
      fontSize: '18px',
      lineHeight: '1.8',
      color: 'var(--text-primary)'
    }}
    dangerouslySetInnerHTML={{ __html: post.content }}
  />
) : hasArrayContent ? (
  <div className="blog-content">
    <PortableText
      value={post.content}
      components={portableTextComponents}
    />
  </div>
) : (
  <p className="body-regular" style={{ 
    padding: '24px',
    background: 'var(--bg-secondary)',
    borderRadius: '0px',
    color: 'var(--text-secondary)'
  }}>
    Full article content coming soon. For more information about this topic,
    please contact us or book a consultation.
  </p>
)}
</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
        <div className="container">
          <div style={{
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Have Questions About This Topic?
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '32px',
              color: 'var(--text-secondary)'
            }}>
              Schedule a consultation with Dr. Parmila Mahala to discuss your dental health
            </p>
            <Link to="/book-appointment" className="btn-primary">
              Book Consultation
            </Link>
          </div>
        </div>
      </section>

      <style jsx>{`
        .blog-content h2 {
          font-size: 32px;
          font-weight: 500;
          margin-top: 48px;
          margin-bottom: 24px;
          color: var(--text-primary);
        }
        
        .blog-content h3 {
          font-size: 24px;
          font-weight: 500;
          margin-top: 32px;
          margin-bottom: 16px;
          color: var(--text-primary);
        }
        
        .blog-content p {
          margin-bottom: 24px;
          line-height: 1.8;
        }
        
        .blog-content ul {
          list-style-type: disc !important;
          padding-left: 32px;
          margin-bottom: 24px;
        }
        
        .blog-content ol {
          list-style-type: decimal !important;
          padding-left: 32px;
          margin-bottom: 24px;
        }
        
        .blog-content li {
          display: list-item;
          margin-bottom: 12px;
          line-height: 1.7;
        }
        
        .blog-content blockquote {
          border-left: 4px solid var(--interactive-base);
          padding-left: 24px;
          margin: 32px 0;
          font-style: italic;
          color: var(--text-secondary);
        }
        
        .blog-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 32px 0;
        }
        
        .blog-content th, .blog-content td {
          border: 1px solid var(--border-medium);
          padding: 12px;
          text-align: left;
        }
        
        .blog-content th {
          background: var(--bg-secondary);
          font-weight: 600;
        }
        
        .blog-content strong {
          font-weight: 600;
          color: var(--text-primary);
        }
        
        @media (max-width: 768px) {
          .blog-content h2 {
            font-size: 24px;
          }
          
          .blog-content h3 {
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogPost;
